import React, { createContext, useContext } from 'react';
import type {ReactNode} from 'react';
import { useTranslation } from 'react-i18next';
import { toApiLanguageCode } from '../utils/languageMapper';

interface LanguageContextType {
  currentLanguage: string;
  changeLanguage: (lng: string) => void;
  t: (key: string, options?: any) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { i18n, t } = useTranslation();

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    // Sync template language (lang_code) so carousels and API calls stay aligned
    const langCode = toApiLanguageCode(lng);
    try {
      localStorage.setItem('ainovis_template_language', langCode);
      window.dispatchEvent(new CustomEvent('templateLanguageChanged', { detail: langCode }));
    } catch { /* storage unavailable */ }
  };

  const value = {
    currentLanguage: i18n.language,
    changeLanguage,
    t,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Hook personalizado para usar el contexto de idioma
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
